import React from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdCloudUpload,
  MdCheckCircle,
  MdError,
  MdSync,
  MdDeleteOutline,
  MdAdd,
  MdImage,
  MdWarning,
  MdInfoOutline,
  MdInsertDriveFile,
} from "react-icons/md";
import {
  useBulkProductImagesUploadModal,
  formatFileSize,
} from "../../customHook/BulkProductImagesUploadModal/useBulkProductImagesUploadModal";

export default function BulkProductImagesUploadModal({
  isOpen,
  onClose,
  onRefreshProducts,
}) {
  const {
    fileInputRef,
    files,
    activeTab,
    setActiveTab,
    isDragOver,
    isUploading,
    uploadResult,
    validFiles,
    invalidFiles,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileSelect,
    handleRemoveFile,
    handleClearAll,
    handleUpload,
    handleCloseModal,
    clearUploadResult,
  } = useBulkProductImagesUploadModal({ isOpen, onClose, onRefreshProducts });

  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isUploading) {
        handleCloseModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isUploading, handleCloseModal]);

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/60  flex items-center justify-center z-[1000] p-4 sm:p-6 transition-opacity"
      onClick={handleCloseModal}
    >
      <div
        className="bg-white border border-slate-200/90 rounded-lg max-w-[920px] w-full min-h-[600px] max-h-[92vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sleek Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center shadow-xs">
              <MdImage size={20} />
            </div>
            <div>
              <h2 className="m-0 text-slate-900 text-base sm:text-lg font-bold tracking-tight">
                {uploadResult ? "Bulk Image Mapping Results" : "Bulk Upload Product Images"}
              </h2>
              <p className="m-0 text-slate-500 text-xs font-normal mt-0.5">
                Batch match & map product front/back media by SKU ID
              </p>
            </div>
          </div>

          <button
            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors flex items-center justify-center border-0 cursor-pointer"
            onClick={handleCloseModal}
            disabled={isUploading}
            type="button"
            aria-label="Close"
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto bg-slate-50/40">
          {/* Always mounted hidden file input so file picker can be opened directly from anywhere */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".png,.jpg,.jpeg"
            className="hidden"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          {/* Post-Upload Summary View */}
          {uploadResult ? (
            <div className="flex flex-col gap-4 animate-in fade-in duration-200">
              {/* Summary Status Banner */}
              <div
                className={`p-3.5 rounded-lg border flex items-center gap-3 animate-in fade-in duration-150 ${
                  uploadResult.status === "error" ||
                  (uploadResult.data?.failedCount > 0 &&
                    (!uploadResult.data?.succeededCount ||
                      uploadResult.data?.succeededCount === 0))
                    ? "bg-red-50/90 border-red-200 text-red-950"
                    : uploadResult.data?.failedCount > 0
                    ? "bg-amber-50/90 border-amber-200 text-amber-950"
                    : "bg-emerald-50/90 border-emerald-200 text-emerald-950"
                }`}
              >
                {uploadResult.status === "error" ||
                (uploadResult.data?.failedCount > 0 &&
                  (!uploadResult.data?.succeededCount ||
                    uploadResult.data?.succeededCount === 0)) ? (
                  <MdError size={22} className="text-red-600 shrink-0" />
                ) : uploadResult.data?.failedCount > 0 ? (
                  <MdWarning size={22} className="text-amber-600 shrink-0" />
                ) : (
                  <MdCheckCircle
                    size={22}
                    className="text-emerald-600 shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="m-0 text-sm font-bold">
                    {uploadResult.status === "error" ||
                    (uploadResult.data?.failedCount > 0 &&
                      (!uploadResult.data?.succeededCount ||
                        uploadResult.data?.succeededCount === 0))
                      ? "Bulk Image Mapping Failed"
                      : uploadResult.data?.failedCount > 0
                      ? "Bulk Image Mapping Completed with Errors"
                      : "Bulk Image Mapping Completed Successfully"}
                  </h3>
                </div>
              </div>

              {/* Stat Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-slate-200 rounded-lg p-3 text-center shadow-xs">
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Total SKUs Processed
                  </span>
                  <span className="block text-xl font-extrabold text-slate-900 mt-1">
                    {uploadResult?.data?.totalSkus ?? 0}
                  </span>
                </div>
                <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 text-center shadow-xs">
                  <span className="block text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                    Succeeded
                  </span>
                  <span className="block text-xl font-extrabold text-emerald-700 mt-1">
                    {uploadResult?.data?.succeededCount ?? 0}
                  </span>
                </div>
                <div
                  className={`${
                    uploadResult?.data?.failedCount > 0
                      ? "bg-red-50/60 border-red-200"
                      : "bg-slate-50 border-slate-200"
                  } border rounded-lg p-3 text-center shadow-xs`}
                >
                  <span
                    className={`block text-[10px] font-bold uppercase tracking-wider ${
                      uploadResult?.data?.failedCount > 0
                        ? "text-red-700"
                        : "text-slate-400"
                    }`}
                  >
                    Failed
                  </span>
                  <span
                    className={`block text-xl font-extrabold mt-1 ${
                      uploadResult?.data?.failedCount > 0
                        ? "text-red-700"
                        : "text-slate-900"
                    }`}
                  >
                    {uploadResult?.data?.failedCount ?? 0}
                  </span>
                </div>
              </div>

              {/* Successful Products Table */}
              {uploadResult?.data?.successfulProducts?.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <MdCheckCircle className="text-emerald-600" size={15} />
                    <span>Updated Products ({uploadResult?.data?.successfulProducts?.length || 0})</span>
                  </span>
                  <div className="bg-white border border-slate-200 rounded-md overflow-hidden max-h-52 overflow-y-auto shadow-2xs">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="py-2.5 px-4 w-32">SKU ID</th>
                          <th className="py-2.5 px-4">PRODUCT TITLE</th>
                          <th className="py-2.5 px-4 text-center w-24">FRONT</th>
                          <th className="py-2.5 px-4 text-center w-24">BACK</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {uploadResult?.data?.successfulProducts?.map((prod, idx) => (
                          <tr key={prod.iProductId || prod.sku || idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-2.5 px-4 font-mono font-semibold text-slate-900 text-[11px]">
                              {prod.sku}
                            </td>
                            <td className="py-2.5 px-4 font-medium text-slate-800">
                              {prod.title || "—"}
                            </td>
                            <td className="py-2.5 px-4 text-center">
                              {prod.frontImage ? (
                                <img
                                  src={prod.frontImage}
                                  alt="Front"
                                  className="w-8 h-10 object-cover rounded-md border border-slate-200 mx-auto"
                                />
                              ) : (
                                <span className="text-[10px] text-slate-400 font-mono">—</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center">
                              {prod.backImage ? (
                                <img
                                  src={prod.backImage}
                                  alt="Back"
                                  className="w-8 h-10 object-cover rounded-md border border-slate-200 mx-auto"
                                />
                              ) : (
                                <span className="text-[10px] text-slate-400 font-mono">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Failed Products Table */}
              {uploadResult?.data?.failedProducts?.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-bold text-red-700 uppercase tracking-wider flex items-center gap-1.5">
                    <MdError className="text-red-600" size={15} />
                    <span>Failed Products ({uploadResult?.data?.failedProducts?.length || 0})</span>
                  </span>
                  <div className="bg-white border border-red-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto shadow-xs">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-red-100 sticky top-0 z-10 border-b border-red-200 text-red-900 font-bold uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="py-2.5 px-3 sm:px-4 w-28 sm:w-36 bg-red-100">SKU ID</th>
                          <th className="py-2.5 px-3 sm:px-4 bg-red-100">ERROR DETAILS</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100 bg-white">
                        {uploadResult?.data?.failedProducts?.map((fail, idx) => (
                          <tr key={idx} className="hover:bg-red-50/40 transition-colors">
                            <td className="py-2.5 sm:py-3 px-3 sm:px-4 font-mono font-semibold text-slate-900 text-[11px] align-top break-all">
                              {fail.sku || "—"}
                            </td>
                            <td className="py-2.5 sm:py-3 px-3 sm:px-4 text-red-600 font-medium align-top leading-relaxed break-words">
                              {fail.error || fail.details || fail.message || "Product does not exist in database or mapping failed."}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Unmatched Files Table */}
              {uploadResult?.data?.unmatchedFiles?.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
                    <MdWarning className="text-amber-600" size={15} />
                    <span>Unmatched Files ({uploadResult?.data?.unmatchedFiles?.length || 0})</span>
                  </span>
                  <div className="bg-white border border-amber-200 rounded-md overflow-hidden max-h-40 overflow-y-auto shadow-2xs">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-amber-50 sticky top-0 z-10 border-b border-amber-200 text-amber-900 font-bold uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="py-2 sm:py-2.5 px-2.5 sm:px-4 w-28 sm:w-44 bg-amber-50">FILENAME</th>
                          <th className="py-2 sm:py-2.5 px-2.5 sm:px-4 bg-amber-50">REASON</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {uploadResult?.data?.unmatchedFiles?.map((unm, idx) => (
                          <tr key={idx} className="hover:bg-amber-50/40">
                            <td className="py-2 sm:py-2.5 px-2.5 sm:px-4 font-mono font-semibold text-slate-900 text-[11px] break-all">
                              {unm.filename}
                            </td>
                            <td className="py-2 sm:py-2.5 px-2.5 sm:px-4 text-amber-800 font-medium">
                              {unm.reason || "Filename does not match SKU format"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>
          ) : (
            /* Upload Pre-Submission View */
            <div className="flex flex-col gap-4">
              {/* Compact Sleek Hint Bar */}
              <div className="bg-white border border-slate-200 rounded-md p-3.5 px-4 flex items-center justify-between gap-3 text-xs text-slate-600 shadow-2xs">
                <div className="flex items-center gap-2 min-w-0">
                  <MdInfoOutline size={18} className="text-slate-500 shrink-0" />
                  <span className="truncate">
                    <strong>Filename Requirement:</strong> Format as{" "}
                    <code className="bg-slate-100 text-slate-900 px-1.5 py-0.5 rounded font-mono font-semibold text-[11px]">
                      SKU;Front.extn
                    </code>{" "}
                    or{" "}
                    <code className="bg-slate-100 text-slate-900 px-1.5 py-0.5 rounded font-mono font-semibold text-[11px]">
                      SKU;Back.extn
                    </code>
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0 text-[11px] font-semibold text-slate-500">
                  <span className="bg-slate-100 px-2 py-0.5 rounded">PNG, JPG, JPEG</span>
                  <span className="bg-slate-100 px-2 py-0.5 rounded">Max 10MB</span>
                </div>
              </div>

              {/* Compact Drop Area */}
              <div
                className={`border ${
                  isDragOver
                    ? "border-slate-400 bg-slate-100"
                    : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                } rounded-md py-4 px-5 cursor-pointer transition-all flex items-center justify-center gap-2.5`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <MdCloudUpload size={18} className="text-slate-500 shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-slate-600">
                  Choose image files or drop here
                </span>
              </div>

              {/* Segmented Control Tabs & Action Bar */}
              {files.length > 0 && (
                <div className="flex flex-col gap-3 mt-1 animate-in fade-in duration-150">
                  {/* SaaS Segmented Pills Header */}
                  <div className="flex items-center justify-between gap-3 flex-wrap bg-white p-2 rounded-md border border-slate-200 shadow-2xs">
                    <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
                      {/* Valid Tab Pill */}
                      <button
                        type="button"
                        onClick={() => setActiveTab("valid")}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border-0 ${
                          activeTab === "valid"
                            ? "bg-white text-slate-900 shadow-xs"
                            : "text-slate-500 hover:text-slate-900 bg-transparent"
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                        <span>Valid Images</span>
                        <span
                          className={`px-1.5 py-0.2 rounded-md text-[10px] font-extrabold ${
                            activeTab === "valid"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-200 text-slate-600"
                          }`}
                        >
                          {validFiles.length}
                        </span>
                      </button>

                      {/* Invalid Tab Pill */}
                      <button
                        type="button"
                        onClick={() => setActiveTab("invalid")}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border-0 ${
                          activeTab === "invalid"
                            ? "bg-white text-slate-900 shadow-xs"
                            : "text-slate-500 hover:text-slate-900 bg-transparent"
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                        <span>Invalid Images</span>
                        <span
                          className={`px-1.5 py-0.2 rounded-md text-[10px] font-extrabold ${
                            activeTab === "invalid"
                              ? "bg-red-100 text-red-800"
                              : "bg-slate-200 text-slate-600"
                          }`}
                        >
                          {invalidFiles.length}
                        </span>
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-xs font-semibold text-slate-700 hover:text-slate-900 transition-colors flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-slate-300 bg-white cursor-pointer"
                      >
                        <MdAdd size={15} />
                        <span>Add Files</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleClearAll}
                        className="text-xs font-semibold text-slate-400 hover:text-red-600 transition-colors flex items-center gap-1 px-2 py-1.5 cursor-pointer bg-transparent border-0"
                      >
                        <MdDeleteOutline size={16} />
                        <span>Clear All</span>
                      </button>
                    </div>
                  </div>

                  {/* Tab Content: Valid Images List */}
                  {activeTab === "valid" && (
                    <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-2xs">
                      {validFiles.length === 0 ? (
                        <div className="py-10 text-center text-slate-400 flex flex-col items-center justify-center gap-1.5">
                          <MdImage size={30} className="text-slate-300" />
                          <p className="m-0 text-xs font-semibold text-slate-600">
                            No valid images in this batch.
                          </p>
                          <p className="m-0 text-[11px] text-slate-400 font-medium">
                            Ensure filenames follow format: <code className="font-mono text-slate-700">Sharara-001;Front.jpg</code>
                          </p>
                        </div>
                      ) : (
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                              <tr>
                                <th className="py-2.5 px-4 w-16">MEDIA</th>
                                <th className="py-2.5 px-4">FILENAME</th>
                                <th className="py-2.5 px-4 w-32">DETECTED SKU</th>
                                <th className="py-2.5 px-4 w-24">SIDE</th>
                                <th className="py-2.5 px-4 w-20">SIZE</th>
                                <th className="py-2.5 px-4 text-right w-16">&nbsp;</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {validFiles.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                                  <td className="py-2 px-4">
                                    {item.previewUrl ? (
                                      <img
                                        src={item.previewUrl}
                                        alt={item.name}
                                        className="w-7 h-9 object-cover rounded-md border border-slate-200"
                                      />
                                    ) : (
                                      <div className="w-7 h-9 bg-slate-100 rounded-md flex items-center justify-center text-slate-400">
                                        <MdInsertDriveFile size={16} />
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-2 px-4 font-mono font-medium text-slate-800 text-[11px] truncate max-w-[220px]" title={item.name}>
                                    {item.name}
                                  </td>
                                  <td className="py-2 px-4 font-mono font-semibold text-slate-900 text-[11px]">
                                    {item.sku}
                                  </td>
                                  <td className="py-2 px-4">
                                    <span
                                      className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                        item.side === "Front"
                                          ? "bg-slate-900 text-white"
                                          : "bg-slate-200 text-slate-800"
                                      }`}
                                    >
                                      {item.side}
                                    </span>
                                  </td>
                                  <td className="py-2 px-4 text-slate-500 font-mono text-[11px]">
                                    {formatFileSize(item.size)}
                                  </td>
                                  <td className="py-2 px-4 text-right">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveFile(item.id)}
                                      className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors p-1.5 rounded-md border-0 cursor-pointer"
                                      title="Remove image"
                                    >
                                      <MdClose size={15} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab Content: Invalid Images List */}
                  {activeTab === "invalid" && (
                    <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-2xs">
                      {invalidFiles.length === 0 ? (
                        <div className="py-10 text-center text-slate-500 flex flex-col items-center justify-center gap-1">
                          <MdCheckCircle size={30} className="text-emerald-500" />
                          <p className="m-0 text-xs font-bold text-slate-800">
                            Zero Validation Errors!
                          </p>
                          <p className="m-0 text-[11px] text-slate-400 font-medium">
                            All uploaded image files passed frontend rules.
                          </p>
                        </div>
                      ) : (
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                              <tr>
                                <th className="py-2.5 px-4">FILENAME</th>
                                <th className="py-2.5 px-4 w-20">SIZE</th>
                                <th className="py-2.5 px-4">VALIDATION ERROR REASON</th>
                                <th className="py-2.5 px-4 text-right w-16">&nbsp;</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {invalidFiles.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                                  <td className="py-2.5 px-4 font-mono font-medium text-slate-900 text-[11px] truncate max-w-[220px]" title={item.name}>
                                    {item.name}
                                  </td>
                                  <td className="py-2.5 px-4 text-slate-500 font-mono text-[11px]">
                                    {formatFileSize(item.size)}
                                  </td>
                                  <td className="py-2.5 px-4">
                                    <span className="inline-flex items-center gap-1.5 text-red-700 bg-red-50 border border-red-200/80 px-2.5 py-1 rounded-md text-[11px] font-medium">
                                      <MdError size={14} className="shrink-0 text-red-500" />
                                      <span>{item.error}</span>
                                    </span>
                                  </td>
                                  <td className="py-2.5 px-4 text-right">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveFile(item.id)}
                                      className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors p-1.5 rounded-md border-0 cursor-pointer"
                                      title="Remove file"
                                    >
                                      <MdClose size={15} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sleek Footer */}
        <div className="px-6 py-3.5 border-t border-slate-100 bg-white flex items-center justify-between shrink-0">
          <div>
            {uploadResult ? null : files.length > 0 ? (
              <span className="text-xs font-medium text-slate-500 flex items-center gap-2">
                <span>Total: <strong className="text-slate-900 font-bold">{files.length}</strong></span>
                <span className="text-slate-300">•</span>
                <span className="text-emerald-700"><strong className="font-bold">{validFiles.length}</strong> valid</span>
                <span className="text-slate-300">•</span>
                <span className="text-red-600"><strong className="font-bold">{invalidFiles.length}</strong> invalid</span>
              </span>
            ) : (
              <span className="text-xs font-normal text-slate-400">
                Ready for image files
              </span>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            {uploadResult ? (
              <>
                <button
                  type="button"
                  className="bg-white border border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-900 text-xs font-semibold px-4 py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <MdCloudUpload size={16} />
                  <span>Upload New Images</span>
                </button>
                <button
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700 text-white border border-blue-600 text-xs font-semibold px-5 py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
                  onClick={handleCloseModal}
                >
                  <MdClose size={15} />
                  <span>Close</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="bg-white border border-slate-300 hover:border-slate-400 text-slate-700 text-xs font-semibold px-4 py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-2xs"
                  onClick={handleCloseModal}
                  disabled={isUploading}
                >
                  <span>Cancel</span>
                </button>

                <button
                  type="button"
                  className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white border border-slate-900 disabled:border-slate-300 text-xs font-semibold px-5 py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed shadow-xs"
                  onClick={handleUpload}
                  disabled={validFiles.length === 0 || isUploading}
                >
                  {isUploading ? (
                    <>
                      <MdSync className="animate-spin" size={16} />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <MdCloudUpload size={16} />
                      <span>Upload</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
